// ReportDownloadButton — кнопка «Выгрузить XLSX» (V14 W9, ТЗ §3.6/§3.4).
// Один запрос GET /api/reports/{id}/xlsx (Q8: файл строится на сервере из того же
// ReportResponse, что JSON-ветка) → blob → downloadBlob с именем из Content-Disposition
// (fallback report-<id>-<yyyyMMdd>.xlsx — канон BE). Ошибки — extractApiErrorMessage.

import { useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { reportsApi, type ReportId, type ReportParams } from '@/api/reports';
import { downloadBlob } from '@/lib/download';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';

interface ReportDownloadButtonProps {
  reportId: ReportId;
  params: ReportParams;
  /** Блокировка кнопки, пока данные отчёта грузятся. */
  disabled?: boolean;
}

/** Кнопка выгрузки XLSX отчёта (loading-состояние; ошибки — Alert). */
export function ReportDownloadButton({ reportId, params, disabled = false }: ReportDownloadButtonProps): JSX.Element {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = async (): Promise<void> => {
    setPending(true);
    setError(null);
    try {
      const { blob, filename } = await reportsApi.downloadXlsx(reportId, params);
      downloadBlob(filename, blob);
    } catch (err) {
      setError(extractApiErrorMessage(err, t('reports.download.error')));
    } finally {
      setPending(false);
    }
  };

  return (
    <Box>
      {error ? (
        <Alert severity="error" sx={{ mb: 1 }} data-testid="reports-download-error">
          {error}
        </Alert>
      ) : null}
      <Button
        variant="outlined"
        startIcon={<FileDownloadIcon />}
        data-testid="report-download"
        disabled={disabled || pending}
        aria-label={t('reports.download.xlsx')}
        onClick={() => void handleDownload()}
      >
        {t('reports.download.xlsx')}
      </Button>
    </Box>
  );
}
